/**
 * The activity log: every action, who took it, and when. Append-only, and open
 * to anyone signed in. Actions read as sentences from the server vocabulary.
 */
import { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useActivityQuery } from '../queries/firm';
import { Card, ErrorNotice } from '../shared/ui';
import { DataTable, type Column } from '../features/shared/tables';
import { PageHeader } from '../provider/pageTitle';
import { useAuth } from '../provider/auth';
import { dateTime, humanise } from '../utils/format';
import type { ActivityEntry } from '../schemas';

const KINDS = [
	{ value: '', label: 'Everything' },
	{ value: 'audit', label: 'Audits' },
	{ value: 'file', label: 'Files' },
	{ value: 'exception', label: 'Exceptions' },
	{ value: 'report', label: 'Reports' },
	{ value: 'analysis_run', label: 'Analyses' },
	{ value: 'classification_rule', label: 'Rules' },
	{ value: 'field_alias', label: 'Column names' },
	{ value: 'ruleset', label: 'Rule sets' },
	{ value: 'user', label: 'People' },
	{ value: 'invite', label: 'Invitations' },
	{ value: 'firm', label: 'Firm' },
];

/**
 * `ActivityLog.detail` as one readable line. It is a jsonb column, not a
 * string, so it cannot go straight into JSX.
 */
function describeDetail(detail: unknown): string {
	if (detail === null || detail === undefined) return '—';
	if (typeof detail === 'string') return detail || '—';
	if (typeof detail !== 'object') return String(detail);

	const parts = Object.entries(detail as Record<string, unknown>)
		.filter(([, v]) => v !== null && v !== undefined && v !== '')
		.map(([k, v]) => {
			const label = k.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
			const value =
				typeof v === 'number' ? v.toLocaleString('en-IN') : String(v);
			return `${label}: ${value}`;
		});
	return parts.length > 0 ? parts.join(' · ') : '—';
}

export default function Activity() {
	const [entity, setEntity] = useState('');
	const { vocabulary } = useAuth();

	const { data, error, isPending, refetch } = useActivityQuery();

	/* The chips above filter what the table shows. */
	const filtered = entity
		? data?.activity.filter((entry) => entry.entity === entity)
		: data?.activity;

	const actionLabel = (code: string) =>
		vocabulary?.activityAction?.[code]?.label ?? humanise(code);

	const columns: Array<Column<ActivityEntry>> = [
		{
			key: 'when',
			label: 'When',
			width: 170,
			sortValue: (row) => row.createdAt,
			render: (row) => (
				<span className="nowrap">{dateTime(row.createdAt)}</span>
			),
		},
		{
			key: 'who',
			label: 'Who',
			width: 180,
			sortValue: (row) => row.actorName ?? '',
			render: (row) =>
				row.actorName ?? <span className="mut">Reconcilio</span>,
		},
		{
			key: 'what',
			label: 'What',
			width: 260,
			sortValue: (row) => actionLabel(row.action),
			render: (row) => actionLabel(row.action),
		},
		{
			key: 'detail',
			label: 'Detail',
			width: 360,
			render: (row) => (
				<span className="small mut">{describeDetail(row.detail)}</span>
			),
		},
	];

	return (
		<Box className="stack">
			<PageHeader title="Activity log" />

			<ToggleButtonGroup
				size="small"
				exclusive
				value={entity}
				onChange={(_event, next) => setEntity(next ?? '')}
				aria-label="Filter the activity log"
			>
				{KINDS.map((kind) => (
					<ToggleButton key={kind.value} value={kind.value}>
						{kind.label}
					</ToggleButton>
				))}
			</ToggleButtonGroup>

			<ErrorNotice error={error} onRetry={refetch} />

			<Card>
				<Box className="card-bd flush">
					<DataTable
						rows={filtered ?? []}
						columns={columns}
						rowKey={(row) => row.pkActivityLogId}
						caption="Activity log"
						loading={isPending}
						initialSort={{ key: 'when', direction: 'desc' }}
						empty="Nothing recorded yet."
					/>
				</Box>
			</Card>
		</Box>
	);
}
